import { FC, ReactNode, useMemo } from "react";

import { Box, Text, VStack } from "@/components/ui";

import DistributionChart from "./DistributionChart";
import HoverCard from "./HoverCard";
import { KPIS, predict } from "./model";
import { AudienceId, Configuration, KpiId } from "./types";
import VerbatimCard from "./VerbatimCard";
import { distribution, verbatims } from "./voice";

/* ── Types ─────────────────────────────────────── */

interface EvidenceHoverProps {
  audience: AudienceId;
  children: ReactNode;
  configuration: Configuration;
  kpi: KpiId;
}

/* ── Constants ─────────────────────────────────── */

const QUOTE_COUNT = 2;

/* ── Component ─────────────────────────────────── */

/** Hover a finding to see the distribution and two verbatims behind it. */
const EvidenceHover: FC<EvidenceHoverProps> = ({
  audience,
  children,
  configuration,
  kpi,
}) => (
  <HoverCard
    content={
      <EvidenceContent
        audience={audience}
        configuration={configuration}
        kpi={kpi}
      />
    }
    width={420}
  >
    {children}
  </HoverCard>
);

/* ── Subcomponents ─────────────────────────────── */

const EvidenceContent: FC<Omit<EvidenceHoverProps, "children">> = ({
  audience,
  configuration,
  kpi,
}) => {
  const definition = KPIS.find((k) => k.id === kpi) ?? KPIS[0];
  const kpis = useMemo(
    () => predict(configuration, audience),
    [audience, configuration]
  );
  const bins = useMemo(
    () => distribution(definition, kpis[kpi]),
    [definition, kpi, kpis]
  );
  const quotes = useMemo(
    () => verbatims(kpi, configuration, audience, kpis, QUOTE_COUNT),
    [audience, configuration, kpi, kpis]
  );
  return (
    <VStack className="gap-3">
      <Text className="text-xs font-medium uppercase tracking-wide text-typography-500">
        {definition.label} · how respondents answered
      </Text>
      <Box className="w-full">
        <DistributionChart bins={bins} />
      </Box>
      <VStack className="gap-2">
        {quotes.map((v, i) => (
          <VerbatimCard clamp={3} key={i} verbatim={v} />
        ))}
      </VStack>
    </VStack>
  );
};

export default EvidenceHover;
